/**
 * Returns true if object is a finite Number.
 * From Underscore.
 * @param {Number|String} number A number
 * @returns {boolean}
 */
export const _isFinite = (number) => {
  if (!number && number !== 0) {
    return false
  } else {
    return isFinite(number) && !isNaN(parseFloat(number))
  }
}

/**
 * Returns a number in Millions.
 *
 * @param {Number|String} number A number
 * @param {Number} decimal Optional decimal points
 * @returns {Number} Number in millions
 */
export const inMillions = (number, decimal) => {
  const scaled = getScaledNumber(number, decimal, {
    scale: Math.pow(10, 6)
  })

  return format(scaled, decimal)
}

/**
 * Returns a number in different scale units (thousands, millions, billions, etc...).
 * This is only used internally.
 *
 * @param {Number} number The number to scale.
 * @param {Number} decimal The decimal places to get fixed.
 * @param {Object} options Parameters
 * @param {Number} options.scale The amount to scale (1000 for thousands, 1000000 for millions, etc...)
 * @returns {Number} The scaled number
 */
export const getScaledNumber = (number, decimal, options) => {

  if (!_isFinite(number)) {
    return number
  }

  let scaledNumber = number / options.scale

  if (typeof decimal === 'number') {
    scaledNumber = toFixed(scaledNumber, decimal)
  }

  return format(scaledNumber)
}

/**
 * Returns a number in a fixed point notation string format.
 *
 * @param {Number} number The number to format.
 * @param {Number} decimal The decimal points. Default: 2
 * @param {Object} options
 * @param {Boolean} options.round If the number should be rounded. Default: true
 * @returns {String} Formatted number
 */
export const toFixed = (number, decimal, options) => {

  options = options || {}
  decimal = (decimal || decimal === 0 ? decimal : 2)

  if (!_isFinite(number)) {
    return number
  }

  if (options.round === false) {
    const regex = new RegExp('^\\-?\\d+(?:\\.\\d{0,' + decimal + '})?', 'g')
    return Number(number).toString().match(regex)
  } else {
    return Number(number).toFixed(decimal)
  }
}

/**
 * Returns a number with comma separated thousands.
 * @param number number A number
 * @param decimal
 * @param format
 * @param dropNegative
 * @returns {String} Number with commas
 */
export const format = (number, decimal, format, dropNegative) => {
  if (format && format === 'dash' && !number) {
    return '-'
  }

  const regex = /^(\d+)(\d{3})/
  const isNegativeMatched = (typeof decimal === 'number' ? Number(number).toFixed(decimal) < 0 : Number(number) < 0)
  let isNegative

  if (!_isFinite(number)) {
    return number
  }

  if (isNegativeMatched) {
    number = Math.abs(Number(number))
    isNegative = true
  }

  if (typeof decimal === 'number') {
    number = Number(number).toFixed(decimal)
    number = Number(number) ? number : '0'
  }

  if (typeof number === 'number') {
    number = number.toString()
  }

  while (regex.test(number)) {
    number = number.replace(regex, '$1,$2')
  }

  if (isNegative === true && !dropNegative) {
    number = '-' + number
  }

  return number
}
